import React, { useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import GenericPopover from "../../../components/GenericPopover";
import ActionIconStrip from "../../../components/ActionIconStrip";
import { AuthContext } from "../../../context/AuthProvider";
import { EditorContext } from "../../context/EditorContext/EditorProvider";
import { CustomMixInfo, InitialTrackData, MainStore } from "../../store";
import { Button } from "../Button";
import { ExitButton } from "../Buttons/ExitButton";
import { MasterVolumeControl } from "../Buttons/MasterVolumeControl";
import { DynamicWaveform } from "../DynamicWaveform";
import { LoadingBar } from "../LoadingBar/LoadingBar";
import { useEditorMainMenu } from "../MainMenuControls";
import { ProjectInfoGrid } from "../ProjectInfoGrid";
import { TransportControls } from "../TransportControls";
import { Container, RightControls } from "./styles";
import useModal from "../../../hooks/useModal";
import { useDownloadMix } from "../../hooks/useDownloadMix";

export let Header = props => {
  const user = useContext(AuthContext);
  const editor = useContext(EditorContext);

  const demoPopover = useModal();

  const { items: mainMenuItems, modals: mainMenuModals } = useEditorMainMenu();

  const initialTrackData = useSelector<MainStore, InitialTrackData>(
    state => state.project.initialTrackData
  );
  const progress = useSelector<MainStore, number>(
    state => state.editorState.renderProgressPercentage
  );
  const isRendering = useSelector<MainStore, boolean>(
    state => state.editorState.isRendering
  );
  const customMixInfo = useSelector<MainStore, CustomMixInfo | null>(
    state => state.project.customMixInfo
  );
  const isCustomMix = useMemo(
    () => customMixInfo && customMixInfo.id !== null,
    [customMixInfo]
  );

  const history = useHistory();
  const { push } = history;

  const downloadMix = useDownloadMix();

  return (
    <Container tightLayout={editor.tightLayout}>
      <div className="player-container">
        <div className="details">
          {initialTrackData.thumbnail && (
            <img
              className="thumbnail"
              src={initialTrackData.thumbnail}
              alt="Album cover"
            />
          )}
          <div
            className="title"
            onClick={() => {
              editor.close();
              const route = isCustomMix
                ? `/project/${customMixInfo.projectId}`
                : `/browse?trackId=${initialTrackData.id}`;
              push(route);
            }}
          >
            {isCustomMix ? customMixInfo.title : initialTrackData.title}
          </div>
          <div
            className="album"
            onClick={() => {
              editor.close();
              push(`/browse?albumId=${initialTrackData.albumId}`);
            }}
          >
            <div className="album-name">
              {initialTrackData.variationTitle
                ? `| ${initialTrackData.variationTitle} `
                : ` ${initialTrackData.albumName} `}
            </div>
          </div>
        </div>
        <TransportControls />
        <DynamicWaveform height={64} />
        <ProjectInfoGrid />
        <RightControls>
          {isRendering && (
            <LoadingBar
              progress={Math.round(progress * 100)}
              title={"Rendering audio"}
              width={115}
              isLoading={isRendering}
            />
          )}
          {editor.isDemo &&
            (!user.isUserPrivate() ||
              (!user.user.paidUser && !user.user.adminUser)) && (
              <GenericPopover
                xOffset="0px"
                yOffset="60px"
                anchor="top"
                popoverState={demoPopover}
                triggerComponent={
                  <Button className="btn-demo" height="auto" px="12">
                    Demo
                  </Button>
                }
                contents={
                  <div id="demo-subscribe-popover">
                    <h4>Subscribe for access to all tracks</h4>
                    <p>
                      The Multitrack Editor is only available to subscribers and
                      license holders.
                    </p>
                    <Button
                      className="btn-subscribe"
                      onClick={() => {
                        editor.close();
                        push("/subscription");
                      }}
                    >
                      Subscribe
                    </Button>
                  </div>
                }
              />
            )}
          <ActionIconStrip
            size="md"
            transparent
            menuPlacement="bottom"
            menuMinWidth="180px"
            actions={[
              {
                type: "volume",
                label: "Volume",
                render: ({ size, style }) => (
                  <MasterVolumeControl key="volume" size={size} style={style} />
                )
              },
              ...(!isRendering
                ? [
                    {
                      type: "download",
                      label: "Download",
                      tooltipText: "Download",
                      onClick: downloadMix
                    }
                  ]
                : []),
              {
                type: "expand_collapse",
                label: "Expand editor",
                // TODO: this tooltip text doesn't seem to be updating
                tooltipText: editor.tightLayout
                  ? "Collapse editor"
                  : "Expand editor",
                onClick: props.onExpand
              }
            ]}
            menuActions={mainMenuItems}
          />
          <ExitButton onClick={editor.close} />
          {mainMenuModals}
        </RightControls>
      </div>
    </Container>
  );
};
